import React, { useEffect, useState } from 'react';

// MUI
import { Grid, Skeleton, styled, Pagination, TextField, Button, useTheme } from '@mui/material';
import { send_request } from '../../../../utils/Request';
import SearchIcon from '@mui/icons-material/Search';
import PlaceholderChip from '../../../common/PlaceholderChip';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import InputAdornment from '@mui/material/InputAdornment';
import { FormControlLabel, Radio, RadioGroup } from '@mui/material';

// Inputs
import ListInput from './ListInput';
import DatetimeInput from './DatetimeInput';
import DefaultInput from './DefaultInput';
import SelectSessionDropdown from './SelectSessionDropdown';
import ApiCurlDisplay from './ApiCurlDisplay';
import { INPUT_DATA_LOCATION_TYPES } from './ForceStartConstants';

// AI Component
import { AIIndividualComponentLoader } from '../../../../utils/AIComponentLoadingSkeleton';

const StyledSkeleton = styled(Skeleton)(({ width, height, margin, theme }) => ({
  borderRadius: '10px',
  background: theme.palette.action.hover,
  width: '100%',
  height: height,
  margin: margin,
}));

const CustomGrid = styled(Grid)(({ vertical, horizontal }) => ({
  display: 'flex',
  alignItems: vertical,
  justifyContent: horizontal,
}));


const Text = styled('span')(({ textColor }) => ({
  fontSize: '12px',
  color: textColor,
}));

const PAGE_SIZE = 25;

const SearchField = styled(TextField)({
  marginBottom: '16px',
  width: '100%',
});

const GenerateAIButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#944BA8',
  color: '#FFFFFF',
  borderRadius: '6px',
  fontSize: '14px',
  fontWeight: 500,
  textTransform: 'none',
  marginBottom: '16px',
  transition: 'background-color 0.2s ease-in-out',
  '&:hover': {
    backgroundColor: '#7B1FA2',
  },
  '&:disabled': {
    backgroundColor: theme.palette.action.disabledBackground,
    color: theme.palette.text.disabled,
  },
}));

const ErrorMessage = styled('div')({
  color: '#d32f2f',
  fontSize: '12px',
  marginTop: '-12px',
  marginBottom: '16px',
});

function InputPlaceholdersBox({
  placeholders,
  setPlaceholders,
  values,
  setValues,
  setIsLoading,
  componentId,
  projectId,
  version,
  inputDataLocation,
  setInputDataLocation,
  isTrigger,
  isTest,
  step,
  fetchTestOutput,
  setFetchTestOutput,
  loadingInputPHs,
  setLoadingInputPHs,
  selected,
  setSelected,
  SHOW_API_OPTION_IN_TEST = true,
}) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredPlaceholders, setFilteredPlaceholders] = useState([]);
  const [isAIGenerating, setIsAIGenerating] = useState(false);
  const [aiGenerateError, setAiGenerateError] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      await fetchPlaceholders(false);
    };
    fetchData();
  }, []);

  useEffect(() => {
    const handleOnFetchTestOutputChange = async () => {
      if (!fetchTestOutput) return;

      if (step !== 2) {
        setFetchTestOutput(false);
        return;
      }

      // Handle re-fetching
      setTimeout(() => {
        setLoadingInputPHs(true);
      }, 0);

      await wait(1000); // Wait 1 second
      await fetchPlaceholders(true);

      setTimeout(() => {
        setLoadingInputPHs(false);
      }, 0);

      setFetchTestOutput(false);
    };
    handleOnFetchTestOutputChange();
  }, [fetchTestOutput]);

  useEffect(() => {
    if (inputDataLocation === INPUT_DATA_LOCATION_TYPES.MANUALLY_SET) {
      if (!values) {
        handleSettingPHValues(placeholders);
      }
      setSelected([null]);
    } else if (inputDataLocation === INPUT_DATA_LOCATION_TYPES.RUN_VIA_API) {
      if (!values) {
        handleSettingPHValues(placeholders);
      }
      setSelected([null]);
    } else {
      setValues(null);
      setSearchTerm('');
      setFilteredPlaceholders([]);
      setSelected(prevSelected => prevSelected.filter(selectItem => selectItem));
    }
  }, [inputDataLocation]);

  // Used for cleanup
  useEffect(() => {
    return () => {
      setPlaceholders(null);
      setValues(null);
      setIsLoading(false);
    };
  }, []);

  const wait = ms =>
    new Promise(res => {
      return setTimeout(res, ms);
    });

  const fetchPlaceholders = async keepOldVals => {
    const url = `component-service/v2/placeholder/${projectId}/${componentId}`;

    const reqData = version ? { workflowVersion: version } : null;

    console.log('[InputPlaceholdersBox] fetchPlaceholders', { url, projectId, componentId, version, reqData });

    const json = await send_request(url, null, reqData, 'get');

    console.log('[InputPlaceholdersBox] response', { json, data: json?.data, dataLength: json?.data?.length });

    if (!json || !json.data || !json.data) {
      console.warn('[InputPlaceholdersBox] No placeholder data returned', { json, hasData: !!json?.data });
      return;
    }
    let pHs = json.data;

    console.log('[InputPlaceholdersBox] placeholders received', pHs);

    handleSettingPHValues(pHs, keepOldVals);

    setPlaceholders(pHs);
  };

  const fetchAiGeneratedPlaceholders = async () => {
    const url = `ai-service/ai/project/generate-test-run-input-values?projectId=${projectId}`;
    return send_request(url, null, null, 'post', undefined, undefined, null, 120000);
  };

  const handleSettingPHValues = (pHs, keepOldVals) => {
    if (!pHs) return;

    let vals = {};

    // Go over placeholders
    for (let pH of pHs) {
      let type = pH.type;

      // Remove the check for keepOldVals to ensure values are always reset
      vals[pH.key] = {
        type: type,
      };

      if (type === 'datetime') {
        const now = new Date();

        vals[pH.key]['value'] = {
          rawValue: now.toISOString(),
          format: 'yyyy-MM-DD hh:MM:ssZ',
          value: now.toISOString().slice(0, 19).replace('T', ' ') + 'Z',
        };
      } else if (type === 'list') vals[pH.key]['value'] = [];
      else vals[pH.key]['value'] = '';
    }

    setValues(vals);
  };

  const updateValueInValues = (placeholderKey, newValue) => {
    let newVal = { ...values };
    let type = newVal?.[placeholderKey]?.type || 'text';

    newVal[placeholderKey] = {
      type: type,
      value: newValue,
    };

    setValues(newVal);
  };

  const getPlaceholderBox = pH => (
    <PlaceholderChip placeholderKey={pH.key} type={pH.type} length={1} wrapText />
  );

  const getCorrectInput = pH => {
    let type = pH.type;
    switch (type) {
      case 'datetime':
        return (
          <DatetimeInput
            pH={pH}
            values={values}
            setValues={setValues}
            updateValueInValues={updateValueInValues}
          />
        );
      case 'list':
        return (
          <ListInput
            pH={pH}
            values={values}
            setValues={setValues}
            updateValueInValues={updateValueInValues}
          />
        );
      default:
        return (
          <DefaultInput
            pH={pH}
            values={values}
            setValues={setValues}
            updateValueInValues={updateValueInValues}
          />
        );
    }
  };

  const getLoader = () => {
    return (
      <Grid container rowGap={2}>
        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(() => {
          return (
            <Grid item xs={12}>
              <StyledSkeleton variant="rectangular" height="50px" />
            </Grid>
          );
        })}
      </Grid>
    );
  };

  useEffect(() => {
    if (!placeholders) return;

    const filtered = placeholders.filter(
      pH =>
        pH.key.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (values &&
          values[pH.key] &&
          values[pH.key].value.toString().toLowerCase().includes(searchTerm.toLowerCase()))
    );
    setFilteredPlaceholders(filtered);
    setPage(1);
  }, [searchTerm, placeholders, values]);

  const handleGenerateWithAI = async () => {
    if (!placeholders) return;

    setIsAIGenerating(true);
    setAiGenerateError(false);

    fetchAiGeneratedPlaceholders()
      .then(response => {
        if (response?.data) {
          setValues(response.data);
          setAiGenerateError(false);
        } else {
          setAiGenerateError(true);
        }
      })
      .catch(() => {
        setAiGenerateError(true);
      })
      .finally(() => {
        setIsAIGenerating(false);
      });
  };

  const SHOW_PLACEHOLDER_VALUES =
    values &&
    selected?.length === 1 &&
    (inputDataLocation === INPUT_DATA_LOCATION_TYPES.MANUALLY_SET ||
      inputDataLocation === INPUT_DATA_LOCATION_TYPES.EXISTING_SESSION ||
      inputDataLocation === INPUT_DATA_LOCATION_TYPES.RUN_VIA_API);

  const SHOW_GENERATE_AI_BUTTON =
    inputDataLocation === INPUT_DATA_LOCATION_TYPES.MANUALLY_SET &&
    placeholders &&
    !loadingInputPHs &&
    !isAIGenerating;

  return (
    <Grid container gap={1}>
      <Grid item xs={12}>
        <b>{!isTrigger ? 'Set input data' : 'Set output data'}</b>
      </Grid>
      <Grid item xs={12}>
        {placeholders ? (
          <RadioGroup
            value={inputDataLocation}
            onChange={e => {
              setInputDataLocation(e.target.value);
              setValues(null);
            }}
          >
            <FormControlLabel
              value={INPUT_DATA_LOCATION_TYPES.MANUALLY_SET}
              control={<Radio size="small" />}
              label={!isTrigger ? 'Manually set input values' : 'Manually set output values'}
            />
            <FormControlLabel
              value={INPUT_DATA_LOCATION_TYPES.EXISTING_SESSION}
              control={<Radio size="small" />}
              label="Use values from an existing session"
            />
            {(!isTest || SHOW_API_OPTION_IN_TEST) && (
              <FormControlLabel
                value={INPUT_DATA_LOCATION_TYPES.RUN_VIA_API}
                control={<Radio size="small" />}
                label="Run via API"
              />
            )}
          </RadioGroup>
        ) : (
          <StyledSkeleton variant="rectangular" height="35px" width="75%" />
        )}
      </Grid>
      {inputDataLocation === INPUT_DATA_LOCATION_TYPES.EXISTING_SESSION && (
        <Grid item xs={12}>
          {placeholders ? (
            <SelectSessionDropdown
              projectId={projectId}
              componentId={componentId}
              StyledSkeleton={StyledSkeleton}
              setValues={setValues}
              values={values}
              placeholders={placeholders}
              setIsLoading={setIsLoading}
              setLoadingInputPHs={setLoadingInputPHs}
              isTest={isTest}
              version={version}
              selected={selected}
              setSelected={setSelected}
            />
          ) : (
            <StyledSkeleton variant="rectangular" height="35px" width="75%" />
          )}
        </Grid>
      )}
      {placeholders && !loadingInputPHs ? (
        <Grid item xs={12}>
          {inputDataLocation === INPUT_DATA_LOCATION_TYPES.RUN_VIA_API ? (
            <ApiCurlDisplay
              placeholders={placeholders}
              values={values}
              projectId={projectId}
              componentId={componentId}
              isTest={isTest}
            />
          ) : (
            <>
              {SHOW_GENERATE_AI_BUTTON && (
                <>
                  <GenerateAIButton
                    variant="contained"
                    size="small"
                    startIcon={<AutoFixHighIcon />}
                    onClick={handleGenerateWithAI}
                    disabled={isAIGenerating}
                  >
                    Generate
                  </GenerateAIButton>
                  {aiGenerateError && (
                    <ErrorMessage>Error generating test values. Please try again.</ErrorMessage>
                  )}
                </>
              )}
              {isAIGenerating && (
                <AIIndividualComponentLoader
                  componentType="force_start"
                  componentName="Input Placeholders"
                  componentDescription="Generating mock data for input placeholders"
                  componentData={{ minimal: true }}
                />
              )}
              {!isAIGenerating && (
                <>
                  {selected?.length <= 1 && (
                    <SearchField
                      variant="outlined"
                      size="small"
                      placeholder="Search placeholders or values..."
                      value={searchTerm}
                      onChange={e => setSearchTerm(e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon />
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                  {SHOW_PLACEHOLDER_VALUES && (
                    <Grid container rowGap={'2px'}>
                      {Object.keys(values).length > 0 && (
                        <Grid item xs={12}>
                          <Grid container marginBottom={'8px'} columnSpacing={'8px'}>
                            <Grid item xs={5}>
                              <Text textColor="var(--color-text-secondary)">Placeholder</Text>
                            </Grid>
                            <Grid item xs={5}>
                              <Text textColor="var(--color-text-secondary)">Value</Text>
                            </Grid>
                          </Grid>
                        </Grid>
                      )}
                      {filteredPlaceholders
                        .filter(pH => !pH.key?.includes('.SECRET'))
                        .slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)
                        .map((pH, idx) => {
                          return (
                            <Grid item xs={12} key={pH.key}>
                              <Grid container marginBottom="4px" columnSpacing={'8px'}>
                                <CustomGrid item xs={5} vertical="center" horizontal="flex-start">
                                  {getPlaceholderBox(pH)}
                                </CustomGrid>
                                <CustomGrid item xs={7} vertical="center" horizontal="flex-start">
                                  {getCorrectInput(pH)}
                                </CustomGrid>
                              </Grid>
                            </Grid>
                          );
                        })}
                      <Grid item xs={12}>
                        <Grid
                          container
                          display="flex"
                          alignItems="flex-end"
                          justifyContent="flex-end"
                          marginTop="4px"
                        >
                          <Pagination
                            color="primary"
                            size="small"
                            showFirstButton
                            showLastButton
                            count={Math.ceil(filteredPlaceholders.length / PAGE_SIZE)}
                            page={page}
                            onChange={(e, value) => setPage(value)}
                          />
                        </Grid>
                      </Grid>
                    </Grid>
                  )}
                </>
              )}
            </>
          )}
        </Grid>
      ) : (
        getLoader()
      )}
    </Grid>
  );
}

export default InputPlaceholdersBox;
